(() => {
  if (window.sokyndVideoLoader) {
    window.sokyndVideoLoader.scan(document);
    return;
  }

  const selector = '[data-sokynd-video-section]';
  const sections = new Map();
  const resources = new Map();
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) initialize(entry.target);
    });
  }, { rootMargin: '500px 0px' }) : null;

  function loadResource(url, type) {
    const href = new URL(url, document.baseURI).href;
    if (resources.has(href)) return resources.get(href);
    const promise = new Promise((resolve, reject) => {
      const existing = type === 'style' ? [...document.querySelectorAll('link[rel="stylesheet"]')].find(link => link.href === href) : null;
      if (existing?.sheet) {
        resolve();
        return;
      }
      const element = existing || document.createElement(type === 'style' ? 'link' : 'script');
      const cleanup = () => {
        element.removeEventListener('load', loaded);
        element.removeEventListener('error', failed);
      };
      const loaded = () => { cleanup(); resolve(); };
      const failed = () => {
        cleanup();
        if (!existing) element.remove();
        reject(new Error(`Unable to load video ${type}`));
      };
      element.addEventListener('load', loaded);
      element.addEventListener('error', failed);
      if (!existing) {
        if (type === 'style') {
          element.rel = 'stylesheet';
          element.href = href;
        } else {
          element.src = href;
          element.async = true;
        }
        document.head.appendChild(element);
      }
    });
    resources.set(href, promise);
    promise.catch(() => resources.delete(href));
    return promise;
  }

  function initialize(section) {
    const state = sections.get(section);
    if (!state || state.loading || state.swiper) return;
    state.loading = true;
    Promise.all([
      loadResource(section.dataset.swiperCss, 'style'),
      window.Swiper ? Promise.resolve() : loadResource(section.dataset.swiperJs, 'script')
    ]).then(() => {
      if (sections.get(section) !== state || !section.isConnected) return;
      const carousel = section.querySelector('.swiper');
      const singleVideo = Number(section.dataset.sokyndVideoCount) === 1;
      state.swiper = carousel.swiper || new window.Swiper(carousel, {
        spaceBetween: 8,
        slidesPerView: 1,
        loop: false,
        allowTouchMove: true,
        freeMode: false,
        lazy: true,
        keyboard: { enabled: true },
        scrollbar: { el: section.querySelector('.video-pagination'), draggable: true },
        breakpoints: singleVideo ? {} : {
          768: { spaceBetween: 16, slidesPerView: 2, slidesPerGroup: 2 },
          992: { spaceBetween: 24, slidesPerView: 4, slidesPerGroup: 4 }
        }
      });
      observer?.unobserve(section);
    }).catch(() => {}).finally(() => { state.loading = false; });
  }

  function bind(section, state, element, event, callback) {
    element.addEventListener(event, callback);
    state.cleanup.push(() => element.removeEventListener(event, callback));
  }

  function scan(container) {
    const roots = [...container.querySelectorAll(selector)];
    if (container.matches?.(selector)) roots.unshift(container);
    roots.forEach(section => {
      if (sections.has(section)) return;
      const state = { loading: false, swiper: null, cleanup: [], videos: [] };
      sections.set(section, state);
      section.querySelectorAll('.video-slide').forEach(slide => {
        const video = slide.querySelector('video');
        const button = slide.querySelector('.video-button');
        if (!video || !button) return;
        state.videos.push(video);
        const sync = () => button.classList.toggle('hidden', !video.paused);
        bind(section, state, video, 'play', sync);
        bind(section, state, video, 'pause', sync);
        bind(section, state, button, 'click', () => {
          if (video.paused) {
            document.querySelectorAll(`${selector} video`).forEach(other => {
              if (other !== video && !other.paused) other.pause();
            });
            const result = video.play();
            result?.catch(sync);
          } else video.pause();
        });
        bind(section, state, slide, 'click', event => {
          if (!button.contains(event.target)) video.pause();
        });
        sync();
      });
      bind(section, state, section, 'pointerdown', () => initialize(section));
      bind(section, state, section, 'focusin', () => initialize(section));
      if (observer) observer.observe(section);
      else initialize(section);
    });
  }

  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    sections.forEach((state, section) => {
      if (event.target !== section && !event.target.contains(section)) return;
      observer?.unobserve(section);
      state.videos.forEach(video => { if (!video.paused) video.pause(); });
      state.cleanup.forEach(cleanup => cleanup());
      state.swiper?.destroy(true, true);
      sections.delete(section);
    });
  });
  window.sokyndVideoLoader = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();
